import type React from 'react';
import { useState } from 'react';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CampaignIcon from '@mui/icons-material/Campaign';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Box, Chip, Collapse, IconButton, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { NOTIFICATION_TYPE } from 'context/NotificationCenterContext';
import type { AppNotification, JobAppNotification } from 'context/NotificationCenterContext';
import {
  GENERAL_NOTIFICATION_THEME,
  getFulfillmentNotificationTitle,
  getFulfillmentThemeForNotification,
  getGlobalEventTheme,
  getNotificationCompanyBodySuffix,
  getNotificationProcessingTypeLabel,
  getNotificationTitle,
  RECENT_NOTIFICATION_THRESHOLD_MS,
} from './NotificationCenter.constants';
import {
  cardInProgressInnerSx,
  cardInProgressSx,
  cardTitleSx,
  descriptionSx,
  fileItemSx,
  filesToggleSx,
  processingTypeChipSx,
  startedTypeChipSx,
  tagsRowSx,
  timestampSx,
  unreadStatusDotSx,
  viewResultsLinkSx,
} from './NotificationCenter.styles';
import { NOTIFICATION_VARIANT } from './NotificationCenter.types';
import {
  formatNotificationRelativeTime,
  getNotificationLinks,
  getNotificationVariant,
  isValidUrl,
} from './NotificationCenter.utils';
import {
  getCardSurfaceSx,
  getNotificationIcon,
  getNotificationIconCircleSx,
  getNotificationTitleColor,
} from './NotificationCenter.variantUi';

/** Strips origin from same-origin absolute URLs so React Router can navigate within the SPA. */
function resolveLinkPath(link: string): string | null {
  try {
    const url = new URL(link);
    if (url.origin === window.location.origin) {
      return url.pathname + url.search + url.hash;
    }
    return null;
  } catch {
    return link.startsWith('/') ? link : null;
  }
}

interface NotificationCardProps {
  notification: AppNotification;
  onDismiss: (id: string) => void;
  /** Mark as read (click on card body); syncs via Ably when possible. */
  onMarkAsRead: (id: string) => void;
  onClose?: () => void;
  nowMs: number;
}

const NotificationCard = ({ notification, onDismiss, onMarkAsRead, onClose, nowMs }: NotificationCardProps) => {
  const [filesOpen, setFilesOpen] = useState(false);

  const { id, read } = notification;
  const relTime = (notification.type === NOTIFICATION_TYPE.GLOBAL || notification.type === NOTIFICATION_TYPE.GENERAL || notification.type === NOTIFICATION_TYPE.FULFILLMENT)
    ? formatNotificationRelativeTime(notification.updatedAt, nowMs)
    : formatNotificationRelativeTime(notification.job.timestamp, nowMs);

  // Fulfillment assignment early return
  if (notification.type === NOTIFICATION_TYPE.FULFILLMENT) {
    const theme = getFulfillmentThemeForNotification(notification);
    const title = getFulfillmentNotificationTitle(notification);
    const linkPath = notification.link ? resolveLinkPath(notification.link) : null;

    return (
      <Box
        data-testid="notification-card"
        sx={{ background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: '12px', p: 1.5, cursor: 'pointer' }}
        role="button"
        tabIndex={0}
        onClick={() => {
          if (!read) {
            onMarkAsRead(id);
          } 
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault(); if (!read) {
              onMarkAsRead(id);
            } 
          }
        }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto auto', gap: 1, alignItems: 'flex-start' }}>
          <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: theme.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <AssignmentIndIcon sx={{ fontSize: 16, color: theme.iconColor }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <Typography sx={{ ...cardTitleSx, color: theme.titleColor }}>{title}</Typography>
              {relTime && <Typography sx={timestampSx}>{relTime}</Typography>}
            </Box>
            {notification.companyName && (
              <Typography sx={{ fontSize: '0.83rem', color: '#374151', mt: 0.5, lineHeight: 1.45 }}>{notification.companyName}</Typography>
            )}
            {linkPath && (
              <Box sx={{ mt: 0.75 }}>
                <Typography
                  component={Link}
                  to={linkPath}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation(); onClose?.(); 
                  }}
                  sx={{ fontSize: '0.78rem', fontWeight: 600, color: theme.titleColor, border: `1px solid ${theme.border}`, borderRadius: '6px', px: 1.25, py: 0.375, textDecoration: 'none', display: 'inline-block', lineHeight: 1.6, '&:hover': { opacity: 0.7 } }}>
                  View Workboard
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ width: 10, display: 'flex', justifyContent: 'center', alignSelf: 'flex-start', pt: 0.625, flexShrink: 0 }} aria-hidden>
            {!read ? <Box sx={unreadStatusDotSx} /> : null}
          </Box>
          <IconButton type="button" size="small" aria-label="Dismiss notification"
            onClick={e => {
              e.preventDefault(); e.stopPropagation(); onDismiss(id); 
            }}
            sx={{ p: 0.25, mt: 0.125 }}>
            <CloseIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </IconButton>
        </Box>
      </Box>
    );
  }

  // Global notification early return
  if (notification.type === NOTIFICATION_TYPE.GLOBAL || notification.type === NOTIFICATION_TYPE.GENERAL) {
    const theme = notification.type === NOTIFICATION_TYPE.GLOBAL
      ? getGlobalEventTheme(notification.eventType)
      : GENERAL_NOTIFICATION_THEME;
    const AnnouncementIcon = notification.type === NOTIFICATION_TYPE.GLOBAL ? CampaignIcon : NotificationsIcon;
    const linkUrl = notification.link && isValidUrl(notification.link) ? notification.link : null;
    const linkLabel = linkUrl ? (notification.linkLabel ?? 'View') : null;

    return (
      <Box
        data-testid="notification-card"
        sx={{ background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: '12px', p: 1.5, cursor: 'pointer' }}
        role="button"
        tabIndex={0}
        onClick={() => {
          if (!read) {
            onMarkAsRead(id);
          }
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!read) {
              onMarkAsRead(id);
            }
          }
        }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto auto', gap: 1, alignItems: 'flex-start' }}>
          <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: theme.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <AnnouncementIcon sx={{ fontSize: 16, color: theme.iconColor }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <Typography sx={{ ...cardTitleSx, color: theme.titleColor }}>{notification.title}</Typography>
              {relTime && <Typography sx={timestampSx}>{relTime}</Typography>}
            </Box>
            <Typography sx={{ fontSize: '0.83rem', color: '#374151', mt: 0.5, lineHeight: 1.45 }}>{notification.content}</Typography>
            {linkUrl && (
              <Box sx={{ mt: 0.75 }}>
                <Box
                  component="a"
                  href={linkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onClose?.();
                  }}
                  sx={{
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    color: theme.titleColor,
                    border: `1px solid ${theme.border}`,
                    borderRadius: '6px',
                    px: 1.25,
                    py: 0.375,
                    textDecoration: 'none',
                    display: 'inline-block',
                    lineHeight: 1.6,
                    '&:hover': { opacity: 0.7 },
                  }}>
                  {linkLabel}
                </Box>
              </Box>
            )}
          </Box>
          <Box sx={{ width: 10, display: 'flex', justifyContent: 'center', alignSelf: 'flex-start', pt: 0.625, flexShrink: 0 }} aria-hidden>
            {!read ? <Box sx={unreadStatusDotSx} /> : null}
          </Box>
          <IconButton type="button" size="small" aria-label="Dismiss notification"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onDismiss(id);
            }}
            sx={{ p: 0.25, mt: 0.125 }}>
            <CloseIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </IconButton>
        </Box>
      </Box>
    );
  }

  // After global/general early return, TypeScript narrows notification to JobAppNotification
  const jobNotification = notification as JobAppNotification;
  const { job, updatedAt } = jobNotification;
  const variant       = getNotificationVariant(notification);
  const isRecent      = nowMs - updatedAt.getTime() < RECENT_NOTIFICATION_THRESHOLD_MS;
  const resultLinks   = getNotificationLinks(notification);
  const resultUrl     = resultLinks?.primary ?? null;

  const { STARTED, PROCESSING, COMPLETED, FAILED } = NOTIFICATION_VARIANT;

  const cardSx = getCardSurfaceSx(variant, isRecent, { read: jobNotification.read });
  // Failed cards always have white background regardless of read state
  const cardSxFinal = variant === FAILED ? { ...cardSx, background: '#fff', backgroundColor: '#fff' } : cardSx;
  const iconCircle = getNotificationIconCircleSx(variant);
  const icon = getNotificationIcon(variant);
  const title = getNotificationTitle(variant);
  const titleColor = getNotificationTitleColor(variant);

  const activateRead = () => {
    if (!jobNotification.read) {
      onMarkAsRead(id);
    }
  };

  const dismissButton = (
    <IconButton
      type="button"
      size="small"
      aria-label="Dismiss notification"
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        onDismiss(id);
      }}
      sx={{ p: 0.25, mt: 0.125 }}>
      <CloseIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
    </IconButton>
  );

  /** Facebook-style: blue dot only while unread; fixed width keeps the close control aligned when read. */
  const unreadColumn = (
    <Box
      sx={{
        width: 10,
        display: 'flex',
        justifyContent: 'center',
        alignSelf: 'flex-start',
        pt: 0.625,
        flexShrink: 0,
      }}
      aria-hidden>
      {!read ? <Box sx={unreadStatusDotSx} /> : null}
    </Box>
  );

  const mainTextColumn = (
    <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minWidth: 0 }}>
        <Typography sx={{ ...cardTitleSx, color: titleColor }}>{title}</Typography>
        {relTime && <Typography sx={timestampSx}>{relTime}</Typography>}
      </Box>

      {(variant === STARTED || variant === PROCESSING) && (
        <>
          {job.companyName && (
            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'text.secondary', mt: 0.5, lineHeight: 1.2 }}>
              {job.companyName}
            </Typography>
          )}
          {job.processingType && (
            <Box sx={{ mt: 0.75 }}>
              <Chip
                label={getNotificationProcessingTypeLabel(job.processingType)}
                size="small"
                sx={variant === STARTED ? startedTypeChipSx : processingTypeChipSx}
              />
            </Box>
          )}
          {job.filenames && job.filenames.length > 0 && (
            <Box mt={0.75}>
              <Box
                component="button"
                sx={filesToggleSx}
                onClick={e => {
                  e.preventDefault(); e.stopPropagation(); setFilesOpen(o => !o);
                }}>
                {filesOpen ? '▾' : '▸'} {job.filenames.length} file{job.filenames.length !== 1 ? 's' : ''}
              </Box>
              <Collapse in={filesOpen}>
                <Box sx={{ mt: 0.5, pl: 1.5, display: 'flex', flexDirection: 'column', gap: 0.25, overflow: 'hidden' }}>
                  {job.filenames.map((file, i) => (
                    <Typography key={i} sx={fileItemSx}>{file}</Typography>
                  ))}
                </Box>
              </Collapse>
            </Box>
          )}
        </>
      )}

      {(variant === COMPLETED || variant === FAILED) && job.companyName && (
        <Typography sx={descriptionSx} mt={0.5}>
          {job.companyName}{' '}
          {getNotificationCompanyBodySuffix(variant)}
        </Typography>
      )}

      {variant === FAILED && job.errorMessage && (
        <Typography sx={{ ...descriptionSx, color: '#b91c1c', fontSize: '0.78rem' }} mt={0.25}>
          {job.errorMessage}
        </Typography>
      )}

      {(variant === COMPLETED || variant === FAILED) && (
        <>
          {job.processingType && (
            <Box sx={tagsRowSx}>
              <Chip
                label={getNotificationProcessingTypeLabel(job.processingType)}
                size="small"
                sx={processingTypeChipSx}
              />
            </Box>
          )}
          {resultLinks && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
              <Typography
                component={Link}
                to={resultLinks.primary}
                onClick={e => {
                  e.stopPropagation(); activateRead(); onClose?.();
                }}
                sx={viewResultsLinkSx}>
                {resultLinks.primaryLabel}
              </Typography>
              {resultLinks.secondary && (
                <Typography
                  component={Link}
                  to={resultLinks.secondary}
                  onClick={e => {
                    e.stopPropagation(); activateRead(); onClose?.();
                  }}
                  sx={viewResultsLinkSx}>
                  {resultLinks.secondaryLabel}
                </Typography>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );

  const iconAndBodyTwoCol = (
    <>
      <Box sx={iconCircle}>{icon}</Box>
      {mainTextColumn}
    </>
  );

  const fourColumnRow = (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto auto', gap: 1, alignItems: 'flex-start' }}>
      {iconAndBodyTwoCol}
      {unreadColumn}
      {dismissButton}
    </Box>
  );

  /** Unread PROCESSING: animated gradient frame (not the flat unread palette from getCardSurfaceSx). */
  if (variant === PROCESSING && !read) {
    return (
      <Box data-testid="notification-card" sx={cardInProgressSx}>
        <Box
          sx={{ ...cardInProgressInnerSx, cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          onClick={activateRead}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              activateRead();
            }
          }}>
          {fourColumnRow}
        </Box>
      </Box>
    );
  }

  /** Do not wrap the dismiss IconButton in <Link> — nested interactive elements navigate incorrectly. */
  if (resultUrl) {
    return (
      <Box
        data-testid="notification-card"
        sx={{
          ...cardSxFinal,
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          gap: 1,
          alignItems: 'flex-start',
        }}>
        <Link
          to={resultUrl}
          onClick={() => {
            activateRead();
            onClose?.();
          }}
          style={{ textDecoration: 'none', display: 'block', color: 'inherit' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: 1,
              alignItems: 'flex-start',
              cursor: 'pointer',
              '&:hover': { opacity: 0.93 },
            }}>
            {iconAndBodyTwoCol}
          </Box>
        </Link>
        {unreadColumn}
        {dismissButton}
      </Box>
    );
  }

  return (
    <Box
      data-testid="notification-card"
      sx={{ ...cardSxFinal, cursor: 'pointer' }}
      role="button"
      tabIndex={0}
      onClick={activateRead}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activateRead();
        }
      }}>
      {fourColumnRow}
    </Box>
  );
};

export default NotificationCard;
